import { makeRESTApiRequest } from '../../helpers/api';
import { validateEmail } from '../../helpers/global';

const ACRONYMS = new Set([
  'LLC', 'INC', 'HOA', 'P.A.', 'CORP', 'LTD', 'CO', 'LP', 'LLP', 'PLLC', 'PC',
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII',
]);

export const resolveEntityId = id => (id && typeof id === 'object' ? id._id : id);

/** Ordering-platform labels processors must not prefix onto directory entity names. */
const FORBIDDEN_DIRECTORY_NAME_TERMS = [
  'Homewise',
  'CondoCerts',
  'Community Archive',
  'Cira Connect',
];

/** e.g. getDirectoryNameError(name, 'Association') → "Association name can't contain Homewise" */
export const getDirectoryNameError = (name, label) => {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return null;
  const hit = FORBIDDEN_DIRECTORY_NAME_TERMS.find(term =>
    normalized.includes(term.toLowerCase())
  );
  return hit ? `${label} name can't contain ${hit}` : null;
};

export const getEmailValidationError = value => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  return validateEmail(trimmed) ? null : 'Enter a valid email';
};

export const isPreferredContactForAssociation = (contact, associationId) => {
  if (!contact) return false;
  if (associationId) {
    return (contact.defaultFor || []).some(id => String(id) === String(associationId));
  }
  return contact.preferred === true;
};

export const pickPreferredContact = (contacts, associationId) =>
  (contacts || []).find(c => isPreferredContactForAssociation(c, associationId)) || null;

const preferredContactPatch = (contact, preferred, associationId) => {
  const assocId =
    associationId != null ? String(resolveEntityId(associationId) ?? associationId) : null;
  if (preferred && assocId) {
    return {
      preferred: false,
      defaultFor: [...new Set([...(contact.defaultFor || []).map(String), assocId])],
    };
  }
  if (preferred) return { preferred: true };
  return {
    preferred: false,
    defaultFor: assocId
      ? (contact.defaultFor || []).filter(id => String(id) !== assocId)
      : [],
  };
};

const mergeDirectoryContact = (contact, directoryById) => {
  const id = contact?._id ? String(contact._id) : null;
  return id && directoryById.has(id) ? { ...contact, ...directoryById.get(id) } : contact;
};

/**
 * Same association re-selected (e.g. after Edit Association) — refresh star badges on
 * already-linked order contacts only. New association links use linkPreferredContactForOrder.
 */
export const syncOrderContactsWithDirectoryPreferred = (
  directoryContacts,
  associationId,
  existingContacts = []
) => {
  const assocId = String(resolveEntityId(associationId) ?? '');
  if (!assocId) return Array.isArray(existingContacts) ? existingContacts : [];

  const directoryById = new Map(
    (directoryContacts || []).filter(c => c?._id).map(c => [String(c._id), c])
  );
  const preferred = pickPreferredContact(directoryContacts, associationId);
  const preferredId = preferred?._id ? String(preferred._id) : null;
  const existing = Array.isArray(existingContacts) ? existingContacts : [];

  const withDirectoryStar = (contact, starred) => {
    const merged = mergeDirectoryContact(contact, directoryById);
    return {
      ...merged,
      ...preferredContactPatch(merged, starred, associationId),
    };
  };

  if (!existing.length) return [];

  const cleared = existing.map(c => withDirectoryStar(c, false));
  if (!preferredId) return cleared;

  const idx = cleared.findIndex(c => c._id && String(c._id) === preferredId);
  if (idx < 0) return cleared;

  const next = [...cleared];
  next[idx] = withDirectoryStar(preferred, true);
  return next;
};

/** Order context — when an association is first linked, auto-link its directory preferred contact. */
export const linkPreferredContactForOrder = (directoryContacts, associationId) => {
  const preferred = pickPreferredContact(directoryContacts, associationId);
  if (!preferred?._id) return [];
  return [
    {
      ...preferred,
      ...preferredContactPatch(preferred, true, associationId),
    },
  ];
};

/** Star/unstar one contact and clear the star on siblings — Edit Association modal only. */
export const applyPreferredToContactList = (
  contacts,
  contact,
  preferred,
  associationId
) =>
  (contacts || []).map(c => {
    if (c === contact) return { ...c, ...preferredContactPatch(c, preferred, associationId) };
    if (preferred && associationId && isPreferredContactForAssociation(c, associationId)) {
      return { ...c, ...preferredContactPatch(c, false, associationId) };
    }
    return c;
  });

/** Linked starred contact phone/email; optional savedAssociation merges fresh directory data. */
export const resolvePreferredPhoneEmail = (hoaSlot, savedAssociation = null) => {
  const associationId = resolveEntityId(savedAssociation?._id ?? hoaSlot?.associationId);
  if (!associationId) {
    return { phoneNumber: hoaSlot?.phoneNumber || '', email: hoaSlot?.email || '' };
  }

  const isSelf = hoaSlot?.managementType === 'self';
  const linked = isSelf ? hoaSlot?.associationContacts : hoaSlot?.managementCompanyContacts;
  const directoryContacts = savedAssociation
    ? (isSelf ? savedAssociation.contacts : savedAssociation.mgmtId?.contacts)
    : [];
  const directoryById = new Map(
    (directoryContacts || []).filter(c => c?._id).map(c => [String(c._id), c])
  );

  const preferred = pickPreferredContact(
    (linked || []).map(c => mergeDirectoryContact(c, directoryById)),
    associationId
  );
  if (preferred) {
    return { phoneNumber: preferred.directPhone || '', email: preferred.directEmail || '' };
  }

  const mgmt =
    savedAssociation?.mgmtId && typeof savedAssociation.mgmtId === 'object'
      ? savedAssociation.mgmtId
      : null;

  return isSelf
    ? {
        phoneNumber:
          savedAssociation?.phone || hoaSlot?.associationPhoneNumber || hoaSlot?.phoneNumber || '',
        email: savedAssociation?.email || hoaSlot?.associationEmail || hoaSlot?.email || '',
      }
    : {
        phoneNumber: mgmt?.phone || hoaSlot?.companyPhone || hoaSlot?.phoneNumber || '',
        email: mgmt?.email || hoaSlot?.companyEmail || hoaSlot?.email || '',
      };
};

/** Report summary columns — preferred contact on association (self) or company (managed). */
export const getHoaSummaryDisplayPhoneEmail = (hoaSlot, savedAssociation = null) => {
  const { phoneNumber, email } = resolvePreferredPhoneEmail(hoaSlot, savedAssociation);

  return hoaSlot?.managementType === 'self'
    ? { associationPhone: phoneNumber, associationEmail: email }
    : {
        associationPhone: hoaSlot?.associationPhoneNumber,
        associationEmail: hoaSlot?.associationEmail,
        companyPhone: phoneNumber,
        companyEmail: email,
      };
};

export const toTitleCase = str =>
  typeof str === 'string'
    ? str
        .trim()
        .replace(/\s+/g, ' ')
        .split(' ')
        .map(word =>
          ACRONYMS.has(word.toUpperCase())
            ? word.toUpperCase()
            : word[0]?.toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')
    : str;

export const blankHoaSnapshot = () => ({
  name: '',
  phoneNumber: '',
  email: '',
  associationPhoneNumber: '',
  associationEmail: '',
  associationId: null,
  associationContacts: null,
  managementCompany: '',
  managementCompanyId: null,
  companyPhone: '',
  companyEmail: '',
  managementCompanyContacts: null,
  orderingInstructions: '',
  orderingPlatform: '',
  selfOrderingInstructionsDraft: '',
  managedOrderingInstructionsDraft: '',
  noEstoppelNeeded: false,
  noEstoppelNeededExplanation: '',
  autoLinked: false,
});

const resolveManagementType = value =>
  value === 'self' || value === 'managed' ? value : undefined;

/** Read stored association.managementType ('self' | 'managed' | unset). */
export const resolveAssociationManagementType = association => {
  if (!association) return undefined;
  return resolveManagementType(association.managementType);
};

export const normalizeHoaSnapshot = (h = {}) => ({
  ...blankHoaSnapshot(),
  _id: h._id || undefined,
  name: h.name || '',
  phoneNumber: h.phoneNumber || '',
  email: h.email || '',
  associationPhoneNumber:
    h.associationPhoneNumber ||
    (h.managementType === 'self' ? h.phoneNumber : '') ||
    '',
  associationEmail:
    h.associationEmail || (h.managementType === 'self' ? h.email : '') || '',
  associationId: h.associationId || null,
  associationContacts: Array.isArray(h.associationContacts) ? h.associationContacts : null,
  ...(resolveManagementType(h.managementType)
    ? { managementType: resolveManagementType(h.managementType) }
    : {}),
  managementCompany: h.managementCompany || '',
  managementCompanyId: h.managementCompanyId || null,
  companyPhone: h.companyPhone || '',
  companyEmail: h.companyEmail || '',
  managementCompanyContacts: Array.isArray(h.managementCompanyContacts)
    ? h.managementCompanyContacts
    : null,
  orderingInstructions: h.orderingInstructions || '',
  orderingPlatform: h.orderingPlatform || '',
  selfOrderingInstructionsDraft: h.selfOrderingInstructionsDraft || '',
  managedOrderingInstructionsDraft: h.managedOrderingInstructionsDraft || '',
  noEstoppelNeeded: Boolean(h.noEstoppelNeeded),
  noEstoppelNeededExplanation: h.noEstoppelNeededExplanation || '',
  autoLinked: h.autoLinked === true,
  _pendingAssociationFiles: Array.isArray(h._pendingAssociationFiles) ? h._pendingAssociationFiles : [],
  _pendingManagementFiles: Array.isArray(h._pendingManagementFiles) ? h._pendingManagementFiles : [],
  _removedAssociationFileIds: Array.isArray(h._removedAssociationFileIds) ? h._removedAssociationFileIds : [],
  _removedManagementCompanyFileIds: Array.isArray(h._removedManagementCompanyFileIds)
    ? h._removedManagementCompanyFileIds
    : [],
});

export const associationToHoaSnapshot = association =>
  normalizeHoaSnapshot({
    name: association.name || '',
    phoneNumber: association.phone || '',
    email: association.email || '',
    associationPhoneNumber: association.phone || '',
    associationEmail: association.email || '',
    associationId: association._id,
    associationContacts: association.contacts || [],
    managementType: resolveAssociationManagementType(association),
    managementCompany: association.mgmtId?.name || '',
    managementCompanyId: association.mgmtId?._id || null,
    companyPhone: association.mgmtId?.phone || '',
    companyEmail: association.mgmtId?.email || '',
    managementCompanyContacts: association.mgmtId?.contacts || [],
    orderingInstructions: association.mgmtId
      ? association.mgmtId.orderingNote
      : association.selfNote || '',
    orderingPlatform: association.orderingPlatform || '',
    noEstoppelNeeded: Boolean(association.noEstoppelNeeded),
  });

export const hasSelfManagedAssociationData = hoa =>
  Boolean(
    hoa.phoneNumber?.trim() ||
      hoa.email?.trim() ||
      hoa.orderingPlatform?.trim() ||
      hoa.orderingInstructions?.trim() ||
      hoa.associationContacts?.length ||
      hoa._pendingAssociationFiles?.length
  );

export const buildAssociationCreateHoa = initialName =>
  normalizeHoaSnapshot({
    ...blankHoaSnapshot(),
    name: toTitleCase(initialName || ''),
  });

export const buildManagementCompanyCreateHoa = initialName =>
  normalizeHoaSnapshot({
    ...blankHoaSnapshot(),
    managementType: 'managed',
    managementCompany: toTitleCase(initialName || ''),
  });

const serializeDirectoryContact = contact => {
  if (!contact || typeof contact !== 'object') return contact;
  const id = resolveEntityId(contact._id ?? contact);
  if (!id && !contact.name && !contact.directEmail && !contact.directPhone) {
    return null;
  }
  return {
    ...(id ? { _id: String(id) } : {}),
    name: contact.name || '',
    title: contact.title || '',
    directPhone: contact.directPhone || '',
    directEmail: contact.directEmail || '',
    ...(Array.isArray(contact.defaultFor) && contact.defaultFor.length
      ? {
          defaultFor: contact.defaultFor
            .map(entry => resolveEntityId(entry))
            .filter(Boolean)
            .map(String),
        }
      : {}),
    ...(contact.preferred === true ? { preferred: true } : {}),
  };
};

export const prepareAssociationPayload = hoa => {
  const orderingPlatform = String(hoa.orderingPlatform || '').trim();
  const isManaged = hoa.managementType === 'managed' && Boolean(hoa.managementCompany?.trim());

  return {
    name: hoa.name.trim(),
    phone: hoa.phoneNumber || '',
    email: hoa.email || '',
    contacts: Array.isArray(hoa.associationContacts)
      ? hoa.associationContacts.map(serializeDirectoryContact).filter(Boolean)
      : [],
    orderingPlatform,
    noEstoppelNeeded: Boolean(hoa.noEstoppelNeeded),
    managementType:
      hoa.managementType === 'self' || hoa.managementType === 'managed'
        ? hoa.managementType
        : null,
    selfNote: hoa.managementType === 'self' ? hoa.orderingInstructions : '',
    _removedAssociationFileIds: hoa._removedAssociationFileIds || [],
    _removedManagementCompanyFileIds: hoa._removedManagementCompanyFileIds || [],
    mgmt: isManaged
      ? {
          ...(hoa.managementCompanyId ? { _id: hoa.managementCompanyId } : {}),
          name: hoa.managementCompany.trim(),
          phone: hoa.companyPhone || '',
          email: hoa.companyEmail || '',
          orderingNote: hoa.orderingInstructions || '',
          orderingPlatform,
          contacts: Array.isArray(hoa.managementCompanyContacts)
            ? hoa.managementCompanyContacts.map(serializeDirectoryContact).filter(Boolean)
            : [],
        }
      : undefined,
  };
};

export const managementCompanyToHoaSnapshot = company =>
  normalizeHoaSnapshot({
    managementType: 'managed',
    managementCompany: company?.name || '',
    managementCompanyId: company?._id || null,
    companyPhone: company?.phone || '',
    companyEmail: company?.email || '',
    orderingInstructions: company?.orderingNote || '',
    managementCompanyContacts: company?.contacts || [],
  });

export const prepareManagementCompanyPayload = hoa => ({
  name: hoa.managementCompany.trim(),
  phone: hoa.companyPhone || '',
  email: hoa.companyEmail || '',
  orderingNote: hoa.orderingInstructions || '',
  contacts: Array.isArray(hoa.managementCompanyContacts)
    ? hoa.managementCompanyContacts.map(serializeDirectoryContact).filter(Boolean)
    : [],
  _removedManagementCompanyFileIds: hoa._removedManagementCompanyFileIds || [],
});

export const uploadInstructionFiles = async (endpoint, files) => {
  for (const file of files || []) {
    if (!(file instanceof File)) continue;
    const formData = new FormData();
    formData.append('file', file, file.name);
    const { error } = await makeRESTApiRequest({
      endpoint,
      method: 'POST',
      contentType: 'multipart/form-data',
      stringify: false,
      requestBody: formData,
    });
    if (error) throw new Error(error);
  }
};
